import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
import { AlertTriangle, Shield, Network, Target, Calendar, TrendingUp, TrendingDown, Minus, Layers, Server, Database, Globe, Lock, Unlock, RefreshCw, Download, FileText, Filter, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
// Mock data - in a real implementation, this would come from your API
const mockPredictedThreats = [{
  id: 'threat1',
  category: 'account_compromise',
  threatType: 'credential_theft',
  description: 'Credential theft through phishing or password reuse',
  likelihood: 0.85,
  impact: 0.75,
  priorityScore: 82,
  confidence: 0.78,
  predictedTimestamp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_authentication_service', 'asset_user_directory'],
  potentialAttackVectors: ['Phishing emails targeting employees', 'Password reuse from breached sites'],
  mitigationRecommendations: ['Implement multi-factor authentication for all user accounts', 'Conduct regular phishing awareness training', 'Deploy anti-phishing email filters'],
  evidenceFactors: ['Identity & access controls below recommended threshold (65%)', '3 account compromise attempts detected recently', 'High vulnerability exposure (72%)']
}, {
  id: 'threat2',
  category: 'data_exfiltration',
  threatType: 'data_exfiltration',
  description: 'Unauthorized data exfiltration through API abuse',
  likelihood: 0.72,
  impact: 0.88,
  priorityScore: 78,
  confidence: 0.82,
  predictedTimestamp: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_customer_database', 'asset_api_gateway'],
  potentialAttackVectors: ['Abuse of legitimate API access', 'Exfiltration via encrypted channels to avoid detection'],
  mitigationRecommendations: ['Implement data loss prevention controls', 'Set up alerts for unusual data access patterns', 'Apply rate limiting on API endpoints'],
  evidenceFactors: ['Data protection controls below recommended threshold (58%)', 'High data sensitivity score (85%)', 'Significant external attack surface exposure (76%)']
}, {
  id: 'threat3',
  category: 'privilege_escalation',
  threatType: 'privilege_escalation',
  description: 'Privilege escalation through misconfigured permissions',
  likelihood: 0.65,
  impact: 0.82,
  priorityScore: 71,
  confidence: 0.75,
  predictedTimestamp: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_admin_portal', 'asset_iam_system'],
  potentialAttackVectors: ['Exploitation of misconfigured role permissions', 'Session hijacking of administrative users'],
  mitigationRecommendations: ['Review and enforce least privilege principles', 'Implement just-in-time access for administrative functions', 'Audit permission changes regularly'],
  evidenceFactors: ['2 privilege escalation incidents in recent history', 'High vulnerability exposure (68%)', 'Elevated threat landscape score (73%)']
}, {
  id: 'threat4',
  category: 'malicious_ip',
  threatType: 'malicious_ip_access',
  description: 'Access attempts from known malicious IP addresses',
  likelihood: 0.78,
  impact: 0.62,
  priorityScore: 68,
  confidence: 0.88,
  predictedTimestamp: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_web_application', 'asset_api_gateway'],
  potentialAttackVectors: ['Known malicious IP addresses attempting to access systems', 'Tor exit nodes used to mask origin'],
  mitigationRecommendations: ['Implement IP reputation filtering', 'Block access from high-risk countries', 'Deploy web application firewall'],
  evidenceFactors: ['Elevated threat landscape score (82%)', '5 malicious IP detections in recent history', 'High vulnerability exposure (65%)']
}, {
  id: 'threat5',
  category: 'suspicious_automation',
  threatType: 'automated_attack',
  description: 'Scripted API abuse to extract data or create accounts',
  likelihood: 0.7,
  impact: 0.68,
  priorityScore: 65,
  confidence: 0.72,
  predictedTimestamp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_api_gateway', 'asset_web_application'],
  potentialAttackVectors: ['Scripted API abuse to extract data or create accounts', 'Credential stuffing using automated tools'],
  mitigationRecommendations: ['Implement rate limiting and throttling', 'Deploy bot detection mechanisms', 'Use CAPTCHA for sensitive operations'],
  evidenceFactors: ['12 security threats detected in last 30 days', 'API protection controls below threshold (62%)', 'Elevated threat landscape score (68%)']
}];
const mockAttackSurfaceMap = {
  assets: [{
    id: 'asset_web_application',
    name: 'Main Web Application',
    type: 'web_application',
    exposureLevel: 1.0,
    businessValue: 0.9,
    containsSensitiveData: true,
    vulnerabilityCount: 3
  }, {
    id: 'asset_api_gateway',
    name: 'API Gateway',
    type: 'api_gateway',
    exposureLevel: 0.9,
    businessValue: 0.85,
    containsSensitiveData: false,
    vulnerabilityCount: 2
  }, {
    id: 'asset_authentication_service',
    name: 'Authentication Service',
    type: 'auth_service',
    exposureLevel: 0.7,
    businessValue: 0.95,
    containsSensitiveData: true,
    vulnerabilityCount: 1
  }, {
    id: 'asset_user_directory',
    name: 'User Directory',
    type: 'directory_service',
    exposureLevel: 0.3,
    businessValue: 0.9,
    containsSensitiveData: true,
    vulnerabilityCount: 0
  }, {
    id: 'asset_customer_database',
    name: 'Customer Database',
    type: 'database',
    exposureLevel: 0.1,
    businessValue: 1.0,
    containsSensitiveData: true,
    vulnerabilityCount: 2
  }],
  entryPoints: ['asset_web_application', 'asset_api_gateway'],
  criticalAssets: ['asset_authentication_service', 'asset_customer_database', 'asset_user_directory'],
  vulnerabilitySummary: {
    critical: 2,
    high: 3,
    medium: 5,
    low: 8
  },
  exposureSummary: {
    internet: 2,
    thirdParty: 1,
    internal: 2
  }
};
const mockAttackChains = [{
  id: 'chain1',
  name: 'Web App to Customer Database',
  description: 'Attack chain from web application to customer database',
  threatCategory: 'data_exfiltration',
  impactScore: 85,
  likelihood: 0.65,
  affectedAssets: ['asset_web_application', 'asset_api_gateway', 'asset_customer_database'],
  attackPaths: [{
    nodes: [{
      assetId: 'asset_web_application',
      assetName: 'Main Web Application',
      step: 0
    }, {
      assetId: 'asset_api_gateway',
      assetName: 'API Gateway',
      step: 1
    }, {
      assetId: 'asset_customer_database',
      assetName: 'Customer Database',
      step: 2
    }],
    edges: [{
      sourceStep: 0,
      targetStep: 1,
      technique: 'API Abuse',
      successProbability: 0.8
    }, {
      sourceStep: 1,
      targetStep: 2,
      technique: 'Data Access',
      successProbability: 0.7
    }],
    exploitability: 0.65,
    complexity: 'medium'
  }]
}, {
  id: 'chain2',
  name: 'Authentication Service Compromise',
  description: 'Attack chain targeting authentication service',
  threatCategory: 'account_compromise',
  impactScore: 90,
  likelihood: 0.55,
  affectedAssets: ['asset_web_application', 'asset_authentication_service', 'asset_user_directory'],
  attackPaths: [{
    nodes: [{
      assetId: 'asset_web_application',
      assetName: 'Main Web Application',
      step: 0
    }, {
      assetId: 'asset_authentication_service',
      assetName: 'Authentication Service',
      step: 1
    }, {
      assetId: 'asset_user_directory',
      assetName: 'User Directory',
      step: 2
    }],
    edges: [{
      sourceStep: 0,
      targetStep: 1,
      technique: 'Authentication Bypass',
      successProbability: 0.6
    }, {
      sourceStep: 1,
      targetStep: 2,
      technique: 'Credential Access',
      successProbability: 0.5
    }],
    exploitability: 0.55,
    complexity: 'high'
  }]
}];
const mockCriticalAssets = [{
  assetId: 'asset_customer_database',
  assetName: 'Customer Database',
  assetType: 'database',
  criticalityScore: 95,
  attackPathCount: 3,
  maxImpactScore: 85,
  vulnerabilityCount: 2,
  isEntryPoint: false,
  isDataRepository: true
}, {
  assetId: 'asset_authentication_service',
  assetName: 'Authentication Service',
  assetType: 'auth_service',
  criticalityScore: 90,
  attackPathCount: 2,
  maxImpactScore: 90,
  vulnerabilityCount: 1,
  isEntryPoint: false,
  isDataRepository: true
}, {
  assetId: 'asset_web_application',
  assetName: 'Main Web Application',
  assetType: 'web_application',
  criticalityScore: 85,
  attackPathCount: 5,
  maxImpactScore: 75,
  vulnerabilityCount: 3,
  isEntryPoint: true,
  isDataRepository: false
}];
// Threat timeline data
const mockTimelineData = [{
  name: '1-7 Days',
  threats: 3,
  critical: 1,
  high: 2
}, {
  name: '8-14 Days',
  threats: 5,
  critical: 2,
  high: 3
}, {
  name: '15-30 Days',
  threats: 8,
  critical: 1,
  high: 4
}, {
  name: '31-90 Days',
  threats: 12,
  critical: 3,
  high: 5
}];
// Asset risk exposure data
const mockAssetRiskData = mockAttackSurfaceMap.assets.map(asset => ({
  name: asset.name,
  exposureLevel: asset.exposureLevel * 100,
  businessValue: asset.businessValue * 100,
  vulnerabilityCount: asset.vulnerabilityCount || 0,
  containsSensitiveData: asset.containsSensitiveData,
  z: 1
}));
// Threat categories
const threatCategories = {
  account_compromise: {
    label: 'Account Compromise',
    color: '#FF6B6B'
  },
  brute_force: {
    label: 'Brute Force',
    color: '#FF9E40'
  },
  data_exfiltration: {
    label: 'Data Exfiltration',
    color: '#4ECDC4'
  },
  privilege_escalation: {
    label: 'Privilege Escalation',
    color: '#A78BFA'
  },
  impossible_travel: {
    label: 'Impossible Travel',
    color: '#F8A102'
  },
  malicious_ip: {
    label: 'Malicious IP',
    color: '#FF5252'
  },
  suspicious_automation: {
    label: 'Suspicious Automation',
    color: '#45B7D1'
  },
  system_tampering: {
    label: 'System Tampering',
    color: '#FF85EA'
  },
  unusual_behavior: {
    label: 'Unusual Behavior',
    color: '#FFC857'
  }
};
// Asset type icons
const assetTypeIcons = {
  web_application: <Globe size={16} data-id="element-5022" />,
  api_gateway: <Server size={16} data-id="element-5023" />,
  auth_service: <Lock size={16} data-id="element-5024" />,
  directory_service: <Layers size={16} data-id="element-5025" />,
  database: <Database size={16} data-id="element-5026" />
};
const PredictiveThreatDashboard = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('month');
  const [loading, setLoading] = useState(false);
  const [predictedThreats, setPredictedThreats] = useState(mockPredictedThreats);
  const [attackSurfaceMap, setAttackSurfaceMap] = useState(mockAttackSurfaceMap);
  const [attackChains, setAttackChains] = useState(mockAttackChains);
  const [criticalAssets, setCriticalAssets] = useState(mockCriticalAssets);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [selectedChain, setSelectedChain] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Fetch data based on timeframe
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would be API calls
        // const threatsResponse = await fetch(`/api/security/predictive/threats?timeframe=${timeframe}`);
        // const threats = await threatsResponse.json();
        // setPredictedThreats(threats);
        // Using mock data for now
        setTimeout(() => {
          setPredictedThreats(mockPredictedThreats);
          setAttackSurfaceMap(mockAttackSurfaceMap);
          setAttackChains(mockAttackChains);
          setCriticalAssets(mockCriticalAssets);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching predictive threat data:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);
  // Filter threats based on category and search query
  const filteredThreats = useMemo(() => {
    return predictedThreats.filter(threat => {
      const matchesCategory = !filterCategory || threat.category === filterCategory;
      const matchesSearch = !searchQuery || threat.description.toLowerCase().includes(searchQuery.toLowerCase()) || threat.threatType.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [predictedThreats, filterCategory, searchQuery]);
  // Handle refresh
  const handleRefresh = () => {
    setLoading(true);
    // In a real implementation, this would trigger a new prediction
    // fetch('/api/security/predictive/analyze', { method: 'POST' })
    //   .then(() => fetchData())
    //   .catch(error => console.error('Error triggering prediction:', error));
    // Using mock data for now
    setTimeout(() => {
      setPredictedThreats(mockPredictedThreats);
      setAttackSurfaceMap(mockAttackSurfaceMap);
      setAttackChains(mockAttackChains);
      setCriticalAssets(mockCriticalAssets);
      setLoading(false);
    }, 1000);
  };
  // Handle report generation
  const handleGenerateReport = reportType => {
    // In a real implementation, this would generate a report
    // window.open(`/api/security/predictive/report/${reportType}`, '_blank');
    alert(`Generating ${reportType} report...`);
  };
  // Format date for display
  const formatDate = date => {
    return new Date(date).toLocaleDateString();
  };
  // Calculate days until predicted event
  const getDaysUntil = date => {
    const now = new Date();
    const predictedDate = new Date(date);
    const diffTime = Math.abs(predictedDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Get threat category information
  const getThreatCategory = category => {
    return threatCategories[category] || {
      label: category,
      color: '#999999'
    };
  };
  // Get asset type icon
  const getAssetTypeIcon = type => {
    return assetTypeIcons[type] || <Server size={16} data-id="element-5027" />;
  };
  // Threat card component
  const ThreatCard = ({
    threat,
    onClick,
    isSelected
  }) => <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 border-l-4 cursor-pointer transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50' : `border-${getThreatCategory(threat.category).color}`}`} style={{
    borderLeftColor: getThreatCategory(threat.category).color
  }} onClick={() => onClick(threat)} data-id="element-5028">
      <div className="flex justify-between items-start" data-id="element-5029">
        <div data-id="element-5030">
          <div className="flex items-center mb-2" data-id="element-5031">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{
            backgroundColor: getThreatCategory(threat.category).color
          }} data-id="element-5032"></span>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5033">
              {getThreatCategory(threat.category).label}
            </h3>
          </div>
          <p className="text-base font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5034">
            {threat.description}
          </p>
          <div className="flex flex-wrap gap-2 mt-2" data-id="element-5035">
            {threat.affectedAssets.slice(0, 2).map((assetId, index) => {
            const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
            return asset ? <div key={index} className="flex items-center text-xs text-gray-600 dark:text-gray-400" data-id="element-5036">
                  {getAssetTypeIcon(asset.type)}
                  <span className="ml-1" data-id="element-5037">{asset.name}</span>
                </div> : null;
          })}
            {threat.affectedAssets.length > 2 && <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-5038">
                +{threat.affectedAssets.length - 2} more
              </div>}
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-5039">
          <div className="flex items-center" data-id="element-5040">
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" data-id="element-5041" />
            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-5042">
              {getDaysUntil(threat.predictedTimestamp)} days
            </span>
          </div>
          <div className={`text-sm font-bold mt-2 px-2 py-1 rounded ${threat.priorityScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : threat.priorityScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5043">
            {threat.priorityScore}
          </div>
        </div>
      </div>
    </div>;
  // Attack chain card component
  const AttackChainCard = ({
    chain,
    onClick,
    isSelected
  }) => <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`} onClick={() => onClick(chain)} data-id="element-5044">
      <div className="flex justify-between items-start" data-id="element-5045">
        <div data-id="element-5046">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-5047">
            {chain.name}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-5048">
            {chain.description}
          </p>
          <div className="flex items-center text-xs" data-id="element-5049">
            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{
            backgroundColor: getThreatCategory(chain.threatCategory).color
          }} data-id="element-5050"></span>
            <span className="text-gray-600 dark:text-gray-400" data-id="element-5051">
              {getThreatCategory(chain.threatCategory).label}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-5052">
          <div className={`text-sm font-bold px-2 py-1 rounded ${chain.impactScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : chain.impactScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5053">
            {chain.impactScore}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-5054">
            {Math.round(chain.likelihood * 100)}% likelihood
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-5055">
        <div className="flex items-center text-xs text-gray-600 dark:text-gray-400" data-id="element-5056">
          <Network className="h-3 w-3 mr-1" data-id="element-5057" />
          <span data-id="element-5058">
            {chain.attackPaths.length} attack path
            {chain.attackPaths.length !== 1 ? 's' : ''}
          </span>
          <span className="mx-2" data-id="element-5059">•</span>
          <span data-id="element-5060">
            {chain.affectedAssets.length} affected asset
            {chain.affectedAssets.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>;
  // Critical asset card component
  const CriticalAssetCard = ({
    asset
  }) => <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4" data-id="element-5061">
      <div className="flex justify-between items-start" data-id="element-5062">
        <div data-id="element-5063">
          <div className="flex items-center mb-1" data-id="element-5064">
            {getAssetTypeIcon(asset.assetType)}
            <h3 className="text-sm font-medium text-gray-900 dark:text-white ml-1" data-id="element-5065">
              {asset.assetName}
            </h3>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-5066">
            {asset.assetType}
          </div>
        </div>
        <div className={`text-sm font-bold px-2 py-1 rounded ${asset.criticalityScore >= 90 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : asset.criticalityScore >= 70 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5067">
          {asset.criticalityScore}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-5068">
        <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-5069">
          <div className="flex items-center text-gray-600 dark:text-gray-400" data-id="element-5070">
            <Network className="h-3 w-3 mr-1" data-id="element-5071" />
            <span data-id="element-5072">
              {asset.attackPathCount} path
              {asset.attackPathCount !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400" data-id="element-5073">
            <AlertTriangle className="h-3 w-3 mr-1" data-id="element-5074" />
            <span data-id="element-5075">
              {asset.vulnerabilityCount} vuln
              {asset.vulnerabilityCount !== 1 ? 's' : ''}
            </span>
          </div>
          {asset.isEntryPoint && <div className="flex items-center text-orange-600 dark:text-orange-400" data-id="element-5076">
              <Unlock className="h-3 w-3 mr-1" data-id="element-5077" />
              <span data-id="element-5078">Entry point</span>
            </div>}
          {asset.isDataRepository && <div className="flex items-center text-purple-600 dark:text-purple-400" data-id="element-5079">
              <Database className="h-3 w-3 mr-1" data-id="element-5080" />
              <span data-id="element-5081">Sensitive data</span>
            </div>}
        </div>
      </div>
    </div>;
  // Threat detail panel
  const ThreatDetailPanel = ({
    threat
  }) => {
    if (!threat) return null;
    return <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5082">
        <div className="flex justify-between items-start mb-4" data-id="element-5083">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5084">
            Threat Details
          </h2>
          <button onClick={() => setSelectedThreat(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-5085">
            <X size={20} data-id="element-5086" />
          </button>
        </div>
        <div className="mb-4" data-id="element-5087">
          <div className="flex items-center mb-2" data-id="element-5088">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{
            backgroundColor: getThreatCategory(threat.category).color
          }} data-id="element-5089"></span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5090">
              {getThreatCategory(threat.category).label}
            </span>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5091">
            {threat.description}
          </h3>
          <div className="flex flex-wrap gap-4 text-sm mb-4" data-id="element-5092">
            <div className="flex items-center" data-id="element-5093">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5094">
                Type:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5095">
                {threat.threatType.replace(/_/g, ' ')}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5096">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5097">
                Predicted:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5098">
                {formatDate(threat.predictedTimestamp)}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5099">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5100">
                Confidence:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5101">
                {Math.round(threat.confidence * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-5102">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5103">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5104">
              Priority Score
            </div>
            <div className="flex items-center" data-id="element-5105">
              <div className={`text-2xl font-bold ${threat.priorityScore >= 80 ? 'text-red-600 dark:text-red-400' : threat.priorityScore >= 60 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5106">
                {threat.priorityScore}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 ml-2" data-id="element-5107">
                /100
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5108">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5109">
              Likelihood
            </div>
            <div className="flex items-center" data-id="element-5110">
              <div className={`text-2xl font-bold ${threat.likelihood >= 0.8 ? 'text-red-600 dark:text-red-400' : threat.likelihood >= 0.6 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5111">
                {Math.round(threat.likelihood * 100)}%
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5112">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5113">
              Impact
            </div>
            <div className="flex items-center" data-id="element-5114">
              <div className={`text-2xl font-bold ${threat.impact >= 0.8 ? 'text-red-600 dark:text-red-400' : threat.impact >= 0.6 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5115">
                {Math.round(threat.impact * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5116">
          <div data-id="element-5117">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5118">
              Affected Assets
            </h4>
            <div className="space-y-2" data-id="element-5119">
              {threat.affectedAssets.map((assetId, index) => {
              const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
              return asset ? <div key={index} className="flex items-center bg-gray-50 dark:bg-gray-700 p-2 rounded" data-id="element-5120">
                    {getAssetTypeIcon(asset.type)}
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5121">
                      {asset.name}
                    </span>
                    {attackSurfaceMap.criticalAssets.includes(assetId) && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full" data-id="element-5122">
                        Critical
                      </span>}
                  </div> : null;
            })}
            </div>
          </div>
          <div data-id="element-5123">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5124">
              Potential Attack Vectors
            </h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-5125">
              {threat.potentialAttackVectors.map((vector, index) => <li key={index} data-id="element-5126">{vector}</li>)}
            </ul>
          </div>
        </div>
        <div className="mb-6" data-id="element-5127">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5128">
            Evidence Factors
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-5129">
            {threat.evidenceFactors.map((factor, index) => <li key={index} data-id="element-5130">{factor}</li>)}
          </ul>
        </div>
        <div data-id="element-5131">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5132">
            Mitigation Recommendations
          </h4>
          <div className="space-y-2" data-id="element-5133">
            {threat.mitigationRecommendations.map((recommendation, index) => <div key={index} className="flex items-start bg-blue-50 dark:bg-blue-900 p-3 rounded" data-id="element-5134">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" data-id="element-5135" />
                <span className="text-sm text-blue-800 dark:text-blue-200" data-id="element-5136">
                  {recommendation}
                </span>
              </div>)}
          </div>
        </div>
      </div>;
  };
  // Attack chain detail panel
  const AttackChainDetailPanel = ({
    chain
  }) => {
    if (!chain) return null;
    return <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5137">
        <div className="flex justify-between items-start mb-4" data-id="element-5138">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5139">
            Attack Chain Details
          </h2>
          <button onClick={() => setSelectedChain(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-5140">
            <X size={20} data-id="element-5141" />
          </button>
        </div>
        <div className="mb-4" data-id="element-5142">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5143">
            {chain.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-5144">
            {chain.description}
          </p>
          <div className="flex flex-wrap gap-4 text-sm" data-id="element-5145">
            <div className="flex items-center" data-id="element-5146">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5147">
                Category:
              </div>
              <div className="flex items-center" data-id="element-5148">
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{
                backgroundColor: getThreatCategory(chain.threatCategory).color
              }} data-id="element-5149"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5150">
                  {getThreatCategory(chain.threatCategory).label}
                </span>
              </div>
            </div>
            <div className="flex items-center" data-id="element-5151">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5152">
                Impact:
              </div>
              <div className={`px-2 py-0.5 rounded ${chain.impactScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : chain.impactScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5153">
                {chain.impactScore}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5154">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5155">
                Likelihood:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5156">
                {Math.round(chain.likelihood * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-5157">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5158">
            Attack Paths
          </h4>
          {chain.attackPaths.map((path, pathIndex) => <div key={pathIndex} className="mb-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5159">
              <div className="flex justify-between items-center mb-3" data-id="element-5160">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5161">
                  Path {pathIndex + 1}
                </div>
                <div className="flex items-center text-xs" data-id="element-5162">
                  <div className="mr-3 text-gray-600 dark:text-gray-400" data-id="element-5163">
                    Exploitability: {Math.round(path.exploitability * 100)}%
                  </div>
                  <div className="text-gray-600 dark:text-gray-400" data-id="element-5164">
                    Complexity: {path.complexity}
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-5165">
                {/* Path nodes and edges visualization */}
                {path.nodes.map((node, nodeIndex) => <div key={nodeIndex} className="flex items-center mb-2" data-id="element-5166">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-800 dark:text-blue-200 font-medium" data-id="element-5167">
                      {nodeIndex + 1}
                    </div>
                    <div className="ml-3 flex-grow bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3" data-id="element-5168">
                      <div className="flex items-center" data-id="element-5169">
                        {getAssetTypeIcon(attackSurfaceMap.assets.find(a => a.id === node.assetId)?.type)}
                        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5170">
                          {node.assetName}
                        </span>
                      </div>
                    </div>
                    {/* Edge/arrow to next node */}
                    {nodeIndex < path.nodes.length - 1 && <div className="absolute left-4 ml-0.5 w-0.5 bg-gray-300 dark:bg-gray-600" style={{
                top: `${nodeIndex * 48 + 32}px`,
                height: '32px'
              }} data-id="element-5171"></div>}
                    {/* Edge label */}
                    {nodeIndex < path.edges.length && <div className="absolute left-12 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded" style={{
                top: `${nodeIndex * 48 + 42}px`
              }} data-id="element-5172">
                        {path.edges[nodeIndex].technique} (
                        {Math.round(path.edges[nodeIndex].successProbability * 100)}
                        %)
                      </div>}
                  </div>)}
              </div>
            </div>)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5173">
          <div data-id="element-5174">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5175">
              Affected Assets
            </h4>
            <div className="space-y-2" data-id="element-5176">
              {chain.affectedAssets.map((assetId, index) => {
              const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
              return asset ? <div key={index} className="flex items-center bg-gray-50 dark:bg-gray-700 p-2 rounded" data-id="element-5177">
                    {getAssetTypeIcon(asset.type)}
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5178">
                      {asset.name}
                    </span>
                    {attackSurfaceMap.criticalAssets.includes(assetId) && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full" data-id="element-5179">
                        Critical
                      </span>}
                  </div> : null;
            })}
            </div>
          </div>
          <div data-id="element-5180">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5181">
              Mitigation Recommendations
            </h4>
            <div className="space-y-2" data-id="element-5182">
              {chain.mitigationRecommendations && chain.mitigationRecommendations.map((recommendation, index) => <div key={index} className="flex items-start bg-blue-50 dark:bg-blue-900 p-3 rounded" data-id="element-5183">
                    <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" data-id="element-5184" />
                    <span className="text-sm text-blue-800 dark:text-blue-200" data-id="element-5185">
                      {recommendation}
                    </span>
                  </div>)}
              {(!chain.mitigationRecommendations || chain.mitigationRecommendations.length === 0) && <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5186">
                  No specific recommendations available for this attack chain.
                </div>}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-id="element-5187">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5188">
        <div className="flex justify-between items-center mb-6" data-id="element-5189">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5190">
            Predictive Threat Modeling
          </h1>
          <div className="flex space-x-4" data-id="element-5191">
            <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-3 text-sm" value={timeframe} onChange={e => setTimeframe(e.target.value)} data-id="element-5192">
              <option value="week" data-id="element-5193">Next 7 Days</option>
              <option value="month" data-id="element-5194">Next 30 Days</option>
              <option value="quarter" data-id="element-5195">Next 90 Days</option>
            </select>
            <button onClick={handleRefresh} className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" disabled={loading} data-id="element-5196">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5197" />
              Refresh
            </button>
            <div className="relative inline-block text-left" data-id="element-5198">
              <div data-id="element-5199">
                <button type="button" className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" id="report-menu" aria-expanded="true" aria-haspopup="true" onClick={() => {
                const menu = document.getElementById('report-menu-items');
                menu.classList.toggle('hidden');
              }} data-id="element-5200">
                  <FileText className="mr-2 h-4 w-4" data-id="element-5201" />
                  Reports
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-5202">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-5203" />
                  </svg>
                </button>
              </div>
              <div className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10" role="menu" aria-orientation="vertical" aria-labelledby="report-menu" id="report-menu-items" data-id="element-5204">
                <div className="py-1" role="none" data-id="element-5205">
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('executive')} data-id="element-5206">
                    Executive Summary
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('technical')} data-id="element-5207">
                    Technical Report
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('attackSurface')} data-id="element-5208">
                    Attack Surface Map
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-5209">
          {/* Predicted Threats Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5210">
            <div className="flex justify-between items-start" data-id="element-5211">
              <div data-id="element-5212">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5213">
                  Predicted Threats
                </p>
                <div className="mt-1" data-id="element-5214">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5215">
                    {predictedThreats.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-red-100 dark:bg-red-900" data-id="element-5216">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-5217" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5218">
              <div className="flex items-center" data-id="element-5219">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5220"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5221">
                  {predictedThreats.filter(t => t.priorityScore >= 80).length}{' '}
                  Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5222">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5223"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5224">
                  {predictedThreats.filter(t => t.priorityScore >= 60 && t.priorityScore < 80).length}{' '}
                  High
                </span>
              </div>
              <div className="flex items-center" data-id="element-5225">
                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-5226"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5227">
                  {predictedThreats.filter(t => t.priorityScore < 60).length}{' '}
                  Medium
                </span>
              </div>
            </div>
          </div>
          {/* Attack Chains Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5228">
            <div className="flex justify-between items-start" data-id="element-5229">
              <div data-id="element-5230">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5231">
                  Attack Chains
                </p>
                <div className="mt-1" data-id="element-5232">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5233">
                    {attackChains.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900" data-id="element-5234">
                <Network className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-5235" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5236">
              <div className="flex items-center" data-id="element-5237">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5238"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5239">
                  {attackChains.filter(c => c.impactScore >= 80).length}{' '}
                  Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5240">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5241"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5242">
                  {attackChains.filter(c => c.impactScore >= 60 && c.impactScore < 80).length}{' '}
                  High
                </span>
              </div>
            </div>
          </div>
          {/* Critical Assets Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5243">
            <div className="flex justify-between items-start" data-id="element-5244">
              <div data-id="element-5245">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5246">
                  Critical Assets
                </p>
                <div className="mt-1" data-id="element-5247">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5248">
                    {criticalAssets.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-blue-100 dark:bg-blue-900" data-id="element-5249">
                <Target className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-5250" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5251">
              <div className="flex items-center" data-id="element-5252">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5253"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5254">
                  {criticalAssets.filter(a => a.criticalityScore >= 90).length}{' '}
                  High Risk
                </span>
              </div>
              <div className="flex items-center" data-id="element-5255">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5256"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5257">
                  {criticalAssets.filter(a => a.criticalityScore < 90).length}{' '}
                  Medium Risk
                </span>
              </div>
            </div>
          </div>
          {/* Vulnerabilities Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5258">
            <div className="flex justify-between items-start" data-id="element-5259">
              <div data-id="element-5260">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5261">
                  Vulnerabilities
                </p>
                <div className="mt-1" data-id="element-5262">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5263">
                    {attackSurfaceMap.vulnerabilitySummary.critical + attackSurfaceMap.vulnerabilitySummary.high + attackSurfaceMap.vulnerabilitySummary.medium + attackSurfaceMap.vulnerabilitySummary.low}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-orange-100 dark:bg-orange-900" data-id="element-5264">
                <Shield className="h-6 w-6 text-orange-600 dark:text-orange-400" data-id="element-5265" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5266">
              <div className="flex items-center" data-id="element-5267">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5268"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5269">
                  {attackSurfaceMap.vulnerabilitySummary.critical} Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5270">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5271"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5272">
                  {attackSurfaceMap.vulnerabilitySummary.high} High
                </span>
              </div>
              <div className="flex items-center" data-id="element-5273">
                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-5274"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5275">
                  {attackSurfaceMap.vulnerabilitySummary.medium} Medium
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-5276">
          {/* Threat Timeline Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5277">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5278">
              Threat Timeline
            </h2>
            <div className="h-80" data-id="element-5279">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5280">
                <BarChart data={mockTimelineData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-5281">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-5282" />
                  <XAxis dataKey="name" data-id="element-5283" />
                  <YAxis data-id="element-5284" />
                  <Tooltip data-id="element-5285" />
                  <Legend data-id="element-5286" />
                  <Bar dataKey="threats" name="All Threats" fill="#8884d8" data-id="element-5287" />
                  <Bar dataKey="critical" name="Critical" fill="#FF3B30" data-id="element-5288" />
                  <Bar dataKey="high" name="High" fill="#FF9500" data-id="element-5289" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Asset Risk Map Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5290">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5291">
              Asset Risk Map
            </h2>
            <div className="h-80" data-id="element-5292">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5293">
                <ScatterChart margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10
              }} data-id="element-5294">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-5295" />
                  <XAxis type="number" dataKey="exposureLevel" name="Exposure Level" unit="%" domain={[0, 100]} label={{
                  value: 'Exposure Level',
                  position: 'bottom',
                  offset: 0
                }} data-id="element-5296" />
                  <YAxis type="number" dataKey="businessValue" name="Business Value" unit="%" domain={[0, 100]} label={{
                  value: 'Business Value',
                  angle: -90,
                  position: 'left'
                }} data-id="element-5297" />
                  <ZAxis type="number" dataKey="vulnerabilityCount" range={[40, 200]} name="Vulnerabilities" data-id="element-5298" />
                  <Tooltip cursor={{
                  strokeDasharray: '3 3'
                }} formatter={(value, name, props) => {
                  if (name === 'Exposure Level' || name === 'Business Value') {
                    return [`${value}%`, name];
                  }
                  return [value, name];
                }} content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white dark:bg-gray-700 p-2 border border-gray-200 dark:border-gray-600 rounded shadow" data-id="element-5300">
                            <p className="font-medium" data-id="element-5301">{data.name}</p>
                            <p data-id="element-5302">Business Value: {data.businessValue}%</p>
                            <p data-id="element-5303">Exposure Level: {data.exposureLevel}%</p>
                            <p data-id="element-5304">Vulnerabilities: {data.vulnerabilityCount}</p>
                            <p data-id="element-5305">
                              Sensitive Data:{' '}
                              {data.containsSensitiveData ? 'Yes' : 'No'}
                            </p>
                          </div>;
                  }
                  return null;
                }} data-id="element-5299" />
                  <Scatter name="Assets" data={mockAssetRiskData} fill={entry => entry.containsSensitiveData ? '#FF3B30' : '#4285F4'} shape="circle" data-id="element-5306" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5307">
          {/* Predicted Threats Column */}
          <div className="lg:col-span-1" data-id="element-5308">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6" data-id="element-5309">
              <div className="flex justify-between items-center mb-4" data-id="element-5310">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-5311">
                  Predicted Threats
                </h2>
                <div className="flex space-x-2" data-id="element-5312">
                  <div className="relative" data-id="element-5313">
                    <button onClick={() => {
                    const menu = document.getElementById('filter-menu');
                    menu.classList.toggle('hidden');
                  }} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5314">
                      <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5315" />
                    </button>
                    <div id="filter-menu" className="hidden absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700" data-id="element-5316">
                      <div className="p-2" data-id="element-5317">
                        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5318">
                          Threat Category
                        </div>
                        <div className="space-y-1" data-id="element-5319">
                          <button onClick={() => setFilterCategory('')} className={`w-full text-left px-2 py-1 text-sm rounded ${filterCategory === '' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-5320">
                            All Categories
                          </button>
                          {Object.entries(threatCategories).map(([key, {
                          label
                        }]) => <button key={key} onClick={() => setFilterCategory(key)} className={`w-full text-left px-2 py-1 text-sm rounded ${filterCategory === key ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-5321">
                                {label}
                              </button>)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="relative" data-id="element-5322">
                    <input type="text" placeholder="Search threats..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5323" />
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" data-id="element-5324" />
                  </div>
                </div>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5325">
                {filteredThreats.map(threat => <ThreatCard key={threat.id} threat={threat} onClick={setSelectedThreat} isSelected={selectedThreat && selectedThreat.id === threat.id} data-id="element-5326" />)}
                {filteredThreats.length === 0 && <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-id="element-5327">
                    No threats match your criteria
                  </div>}
              </div>
            </div>
          </div>
          {/* Details Column */}
          <div className="lg:col-span-2" data-id="element-5328">
            {selectedThreat ? <ThreatDetailPanel threat={selectedThreat} data-id="element-5329" /> : selectedChain ? <AttackChainDetailPanel chain={selectedChain} data-id="element-5330" /> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5331">
                {/* Attack Chains Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-id="element-5332">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5333">
                    Attack Chains
                  </h2>
                  <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5334">
                    {attackChains.map(chain => <AttackChainCard key={chain.id} chain={chain} onClick={setSelectedChain} isSelected={false} data-id="element-5335" />)}
                  </div>
                </div>
                {/* Critical Assets Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-id="element-5336">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5337">
                    Critical Assets
                  </h2>
                  <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5338">
                    {criticalAssets.map(asset => <CriticalAssetCard key={asset.assetId} asset={asset} data-id="element-5339" />)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default PredictiveThreatDashboard;